import {context, propagation, trace} from '@opentelemetry/api';
import type {AxiosError, InternalAxiosRequestConfig} from 'axios';
import axios from 'axios';

import type ErrorResponse from '@/models/ErrorResponse';

import {createSessionWithDeviceDetails} from '../services/auth-apis';

const axiosHandler = axios;

axiosHandler.defaults.withCredentials = true;
axiosHandler.interceptors.request.use((config) => {
  try {
    const carrier: Record<string, string> = {};
    if (process.env.NEXT_PUBLIC_TELEMETRY_DISABLE_ALL === 'false') {
      const currentContext = context.active();
      propagation.inject(
        context.with(trace.setSpan(currentContext, trace.getActiveSpan()), () => currentContext),
        carrier,
      );
      const traceHeaders: Record<string, string> = {};
      Object.entries(carrier).forEach(([key, value]) => {
        if (value) {
          traceHeaders[key] = value as string;
        }
      });
    }
    const headers = new axios.AxiosHeaders(config.headers);
    headers.set(carrier);
    config.headers = headers;
    return config;
  } catch (error) {
    console.error('Error in trace propagation for axios:', error);
  }
  return config;
});

axiosHandler.interceptors.response.use(
  (res: any) => {
    if (typeof window !== 'undefined' && res.data.nextExpiryTime) {
      localStorage.setItem('nextExpiryTime', res.data.nextExpiryTime);
    }
    return res;
  },
  async (err: AxiosError<ErrorResponse>) => {
    const originalRequest = err.config as InternalAxiosRequestConfig<any> & {
      _retry: boolean;
    };
    if (err.response?.data?.errorMessage === 'Invalid session' && originalRequest._retry !== true) {
      await createSessionWithDeviceDetails();
      originalRequest._retry = true;
      return axiosHandler(originalRequest);
    }
    if (err.response?.status === 401) {
      if (typeof window !== 'undefined') {
        if (err.response?.data?.isTimedOut) {
          window.location.replace('/login?reason=sessiontimeout');
        } else {
          sessionStorage.setItem('redirectUrl', window.location.href);
          window.location.replace('/login');
        }
      }
    }
    return Promise.reject(err);
  },
);

export default axiosHandler;
